import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CalendarDays, Plus, Inbox, Trash2, ListFilter, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PageTransition, staggerContainer, fadeInUp } from '../components/PageTransition';
import { LeaveRequestCard } from '../components/leave/LeaveRequestCard';
import { LeaveRequestDialog } from '../components/leave/LeaveRequestDialog';
import { BottomSheet } from '../components/shared/bottom-sheet';
import {
  leaveRequestService,
  LeaveRequest,
  CreateLeaveRequestDto,
} from '../services/leaveRequestService';
import { useNotification } from '../contexts/NotificationContext';

const LeaveRequestCardSkeleton: React.FC = () => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-5 w-16" />
          </div>
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-32" />
        </div>
        <div className="flex gap-1">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const LeaveRequests: React.FC = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<LeaveRequest | null>(null);
  const [processing, setProcessing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<LeaveRequest | null>(null);
  const [cancellationDialogOpen, setCancellationDialogOpen] = useState(false);
  const [requestToCancelApproved, setRequestToCancelApproved] = useState<LeaveRequest | null>(null);
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const data = await leaveRequestService.getMyRequests();
      setRequests(data);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSave = async (data: CreateLeaveRequestDto, submit: boolean) => {
    try {
      setProcessing(true);
      let savedRequest: LeaveRequest;

      if (editingRequest) {
        if (editingRequest.state === 'approved') {
          savedRequest = await leaveRequestService.requestAmendment(editingRequest.id, data);
          showNotification(
            t('leave.messages.amendmentRequested', 'Amendment requested. Changes will require approval.'),
            'success',
          );
        } else {
          savedRequest = await leaveRequestService.update(editingRequest.id, data);
          showNotification(t('leave.messages.updated', 'Leave request updated'), 'success');
        }
      } else {
        if (submit) {
          savedRequest = await leaveRequestService.createAndSubmit(data);
          showNotification(t('leave.messages.submitted', 'Leave request submitted for approval'), 'success');
        } else {
          savedRequest = await leaveRequestService.create(data);
          showNotification(t('leave.messages.created', 'Leave request created'), 'success');
        }
      }

      if (submit && editingRequest && editingRequest.state !== 'approved') {
        await leaveRequestService.submit(savedRequest.id);
        showNotification(t('leave.messages.submitted', 'Leave request submitted for approval'), 'success');
      }

      setDialogOpen(false);
      setEditingRequest(null);
      await fetchRequests();
    } catch (error) {
      console.error('Error saving leave request:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (request: LeaveRequest) => {
    if (request.state === 'approved') {
      showNotification(
        t('leave.messages.amendmentWarning', 'You are requesting a change to an approved leave. The update must be approved again.'),
        'info',
      );
    }
    setEditingRequest(request);
    setDialogOpen(true);
  };

  const handleRequestCancellation = (request: LeaveRequest) => {
    setRequestToCancelApproved(request);
    setCancellationDialogOpen(true);
  };

  const handleConfirmRequestCancellation = async () => {
    if (!requestToCancelApproved) return;
    try {
      setProcessing(true);
      await leaveRequestService.requestCancellation(requestToCancelApproved.id, {
        reason: t('leave.messages.cancellationReasonDefault', 'User requested cancellation after approval'),
      });
      showNotification(
        t('leave.messages.cancellationRequested', 'Cancellation requested. It must be approved.'),
        'success',
      );
      setCancellationDialogOpen(false);
      setRequestToCancelApproved(null);
      await fetchRequests();
    } catch (error) {
      console.error('Error requesting cancellation:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = async (request: LeaveRequest) => {
    try {
      setProcessing(true);
      await leaveRequestService.submit(request.id);
      showNotification(t('leave.messages.submitted', 'Leave request submitted for approval'), 'success');
      await fetchRequests();
    } catch (error) {
      console.error('Error submitting leave request:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteClick = (request: LeaveRequest) => {
    setRequestToDelete(request);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!requestToDelete) return;

    try {
      setProcessing(true);
      await leaveRequestService.delete(requestToDelete.id);
      showNotification(t('leave.messages.deleted', 'Leave request deleted'), 'success');
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
      await fetchRequests();
    } catch (error) {
      console.error('Error deleting leave request:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async (request: LeaveRequest) => {
    try {
      setProcessing(true);
      await leaveRequestService.cancel(request.id);
      showNotification(t('leave.messages.cancelled', 'Leave request cancelled'), 'success');
      await fetchRequests();
    } catch (error) {
      console.error('Error cancelling leave request:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleNewRequest = () => {
    setEditingRequest(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (!processing) {
      setDialogOpen(false);
      setEditingRequest(null);
    }
  };

  const handleDeleteDialogClose = () => {
    if (!processing) {
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
    }
  };

  const filteredRequests = useMemo(() => {
    return requests.filter((request) => {
      const requestCategory = request.category ?? (request.changeType === 'none' ? 'original' : request.changeType);
      const matchesState = stateFilter === 'all' || request.state === stateFilter;
      const matchesType = typeFilter === 'all' || request.type === typeFilter;
      const matchesCategory = categoryFilter === 'all' || requestCategory === categoryFilter;

      return matchesState && matchesType && matchesCategory;
    });
  }, [requests, stateFilter, typeFilter, categoryFilter]);

  const hasActiveFilters = stateFilter !== 'all' || typeFilter !== 'all' || categoryFilter !== 'all';

  const resetFilters = () => {
    setStateFilter('all');
    setTypeFilter('all');
    setCategoryFilter('all');
  };

  return (
    <PageTransition className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <CalendarDays className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {t('leave.myRequests.title', 'My Leave Requests')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('leave.myRequests.subtitle', 'Manage your vacation and permission requests')}
            </p>
          </div>
        </div>
        <Button onClick={handleNewRequest} className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600">
          <Plus className="h-4 w-4 mr-2" />
          {t('leave.newRequest', 'New Request')}
        </Button>
      </motion.div>

      {!loading && requests.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <ListFilter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {t('leave.myRequests.filters.title', 'Filters')}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select value={stateFilter} onValueChange={setStateFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('leave.myRequests.filters.state', 'State')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('leave.myRequests.filters.allStates', 'All states')}</SelectItem>
                    <SelectItem value="draft">{t('leave.states.draft', 'Draft')}</SelectItem>
                    <SelectItem value="pending">{t('leave.states.pending', 'Pending')}</SelectItem>
                    <SelectItem value="approved">{t('leave.states.approved', 'Approved')}</SelectItem>
                    <SelectItem value="rejected">{t('leave.states.rejected', 'Rejected')}</SelectItem>
                    <SelectItem value="cancelled">{t('leave.states.cancelled', 'Cancelled')}</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('leave.myRequests.filters.type', 'Type')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('leave.myRequests.filters.allTypes', 'All types')}</SelectItem>
                    <SelectItem value="vacation">{t('leave.types.vacation', 'Vacation')}</SelectItem>
                    <SelectItem value="permission">{t('leave.types.permission', 'Permission')}</SelectItem>
                    <SelectItem value="sickness">{t('leave.types.sickness', 'Sickness')}</SelectItem>
                    <SelectItem value="rol">{t('leave.types.rol', 'ROL')}</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('leave.myRequests.filters.category', 'Category')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('leave.myRequests.filters.allCategories', 'All categories')}</SelectItem>
                    <SelectItem value="original">{t('leave.myRequests.categories.original', 'Original')}</SelectItem>
                    <SelectItem value="amendment">{t('leave.myRequests.categories.amendment', 'Amendment')}</SelectItem>
                    <SelectItem value="cancellation">{t('leave.myRequests.categories.cancellation', 'Cancellation')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  {t('leave.myRequests.filters.results', '{{count}} requests', { count: filteredRequests.length })}
                </p>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="w-full sm:w-auto">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {t('leave.myRequests.filters.reset', 'Reset filters')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Content */}
      {loading ? (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-4"
        >
          {[1, 2, 3].map((i) => (
            <motion.div key={i} variants={fadeInUp}>
              <LeaveRequestCardSkeleton />
            </motion.div>
          ))}
        </motion.div>
      ) : requests.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card>
            <CardContent className="p-12">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Inbox className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {t('leave.myRequests.empty.title', 'No leave requests')}
                </h3>
                <p className="text-muted-foreground mb-6 max-w-sm">
                  {t('leave.myRequests.empty.description', "You haven't created any leave requests yet. Create your first request to get started.")}
                </p>
                <Button onClick={handleNewRequest} className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('leave.newRequest', 'New Request')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : filteredRequests.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardContent className="p-10">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-4">
                  <ListFilter className="h-7 w-7 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {t('leave.myRequests.filters.empty.title', 'No requests match these filters')}
                </h3>
                <p className="text-muted-foreground mb-5 max-w-sm">
                  {t('leave.myRequests.filters.empty.description', 'Try changing or resetting the filters to see more requests.')}
                </p>
                <Button variant="outline" onClick={resetFilters}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t('leave.myRequests.filters.reset', 'Reset filters')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-4"
        >
          {filteredRequests.map((request, index) => {
            const hasPendingChangeRequest =
              request.state === 'approved' &&
              requests.some(
                (candidate) =>
                  candidate.parentRequestId === request.id &&
                  candidate.state === 'pending',
              );

            return (
              <motion.div
                key={request.id}
                variants={fadeInUp}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <LeaveRequestCard
                  request={request}
                  onEdit={handleEdit}
                  onSubmit={handleSubmit}
                  onDelete={handleDeleteClick}
                  onCancel={handleCancel}
                  onRequestAmendment={hasPendingChangeRequest ? undefined : handleEdit}
                  onRequestCancellation={hasPendingChangeRequest ? undefined : handleRequestCancellation}
                />
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Create/Edit Dialog */}
      <LeaveRequestDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        request={editingRequest}
        loading={processing}
      />

      {/* Delete Confirmation Dialog */}
      <BottomSheet
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        title={t('leave.deleteDialog.title', 'Delete Leave Request')}
        description={t('leave.deleteDialog.description', 'Are you sure you want to delete this leave request? This action cannot be undone.')}
      >
        <div className="flex items-center gap-2 rounded-md border bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-950/30 dark:text-red-300">
          <Trash2 className="h-4 w-4" />
          <span>{t('leave.deleteDialog.title', 'Delete Leave Request')}</span>
        </div>

        <div className="sticky bottom-0 -mx-4 mt-4 flex justify-end gap-2 border-t bg-background px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3">
          <Button
            variant="outline"
            onClick={handleDeleteDialogClose}
            disabled={processing}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteConfirm}
            disabled={processing}
          >
            {processing ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
          </Button>
        </div>
      </BottomSheet>

      {/* Request cancellation confirmation for approved leaves */}
      <BottomSheet
        open={cancellationDialogOpen}
        onClose={() => !processing && setCancellationDialogOpen(false)}
        title={t('leave.requestCancellationTitle', 'Request cancellation')}
        description={t(
          'leave.requestCancellationDescription',
          'This leave is already approved. Cancellation will be sent for a new approval and will not be immediate.',
        )}
      >
        <div className="sticky bottom-0 -mx-4 mt-4 flex justify-end gap-2 bg-background px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3">
          <Button variant="outline" onClick={() => setCancellationDialogOpen(false)} disabled={processing}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="destructive" onClick={handleConfirmRequestCancellation} disabled={processing}>
            {t('leave.requestCancellation', 'Request cancellation')}
          </Button>
        </div>
      </BottomSheet>
    </PageTransition>
  );
};

export default LeaveRequests;
